import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { useAuth } from "@/hooks/use-auth";
import { apiGet, apiPost } from "@/lib/api-client";

export type ChallengeType =
    | "vocabulary_review"
    | "practice_topic"
    | "create_topic"
    | "study_time"
    | "video_shadowing"
    | "video_dictation";

export interface DailyChallenge {
    id: string;
    user_id: string;
    challenge_date: string;
    challenge_type: ChallengeType;
    target_count: number;
    current_count: number;
    xp_reward: number;
    is_completed: boolean;
    is_claimed: boolean;
    created_at: string;
    updated_at: string;
}

export interface IncrementProgressResult {
    updated: boolean;
    is_completed: boolean;
    current_count: number;
    target_count: number;
}

export interface ClaimRewardResult {
    success: boolean;
    xp_awarded: number;
    new_xp: number;
    new_level: number;
    level_up: boolean;
    current_streak: number;
    longest_streak: number;
    streak_updated: boolean;
}

/**
 * Get human-readable challenge description
 */
export const getChallengeDescription = (type: ChallengeType, targetCount: number): string => {
    switch (type) {
        case "vocabulary_review":
            return `Review ${targetCount} vocabulary word${targetCount > 1 ? "s" : ""}`;
        case "practice_topic":
            return `Practice ${targetCount} topic${targetCount > 1 ? "s" : ""}`;
        case "create_topic":
            return `Create ${targetCount} new topic${targetCount > 1 ? "s" : ""}`;
        case "study_time":
            return `Study for ${targetCount} minute${targetCount > 1 ? "s" : ""}`;
        case "video_shadowing":
            return `Practice ${targetCount} video caption${targetCount > 1 ? "s" : ""} (speaking)`;
        case "video_dictation":
            return `Practice ${targetCount} video caption${targetCount > 1 ? "s" : ""} (writing)`;
        default:
            return "Complete the challenge";
    }
};

/**
 * Get challenge icon name (for lucide-react)
 */
export const getChallengeIcon = (type: ChallengeType): string => {
    switch (type) {
        case "vocabulary_review":
            return "BookOpen";
        case "practice_topic":
            return "Mic";
        case "create_topic":
            return "PlusCircle";
        case "study_time":
            return "Clock";
        case "video_shadowing":
            return "Mic";
        case "video_dictation":
            return "PenLine";
        default:
            return "Star";
    }
};

/**
 * Calculate progress percentage for a challenge
 */
export const getChallengeProgress = (challenge: DailyChallenge): number => {
    return Math.min(100, (challenge.current_count / challenge.target_count) * 100);
};

export interface UseDailyChallengesOptions {
    /**
     * Path to `get_or_create_daily_challenges`.
     *
     * That RPC is a **write** (it inserts today's six rows), so this hook must
     * fire exactly once per app load, from `AutoClaimRewards` — the one caller
     * that actually has to act on the answer. Every other caller passes
     * `enabled` tied to whatever surface renders the challenges, so a closed
     * streak dialog no longer triggers a second write on every page.
     */
    enabled?: boolean;
}

/**
 * Hook to manage daily challenges (all 4 types)
 */
export const useDailyChallenges = ({ enabled = true }: UseDailyChallengesOptions = {}) => {
    const { userId } = useAuth();
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: queryKeys.dailyChallenges.byUser(userId),
        enabled: enabled && !!userId,
        queryFn: async (): Promise<DailyChallenge[]> => {
            if (!userId) return [];

            const data = await apiGet<unknown[]>("/v1/gamification/challenges");
            const error = null;

            if (error) {
                console.error("Error fetching daily challenges:", error);
                return [];
            }

            return (data as DailyChallenge[]) || [];
        },
        staleTime: 1000 * 60 * 2, // 2 minutes
    });

    const incrementProgressMutation = useMutation({
        mutationFn: async ({
            challengeType,
            increment = 1,
        }: {
            challengeType: ChallengeType;
            increment?: number;
        }): Promise<IncrementProgressResult | null> => {
            if (!userId) return null;

            const data = await apiPost<unknown[]>(
                `/v1/gamification/challenges/${encodeURIComponent(challengeType)}/progress`,
                { increment },
            );
            const error = null;

            if (error) {
                console.error("Error incrementing challenge progress:", error);
                throw error;
            }

            return data?.[0] as IncrementProgressResult;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.dailyChallenges.byUser(userId) });
        },
    });

    const claimRewardMutation = useMutation({
        mutationFn: async (challengeId: string): Promise<ClaimRewardResult | null> => {
            if (!userId) return null;

            const data = await apiPost<unknown>(
                `/v1/gamification/challenges/${encodeURIComponent(challengeId)}/claims`,
                {},
            ).then((r) => [r]);
            const error = null;

            if (error) {
                console.error("Error claiming challenge reward:", error);
                throw error;
            }

            return data?.[0] as ClaimRewardResult;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.dailyChallenges.byUser(userId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.gamification.byUser(userId) });
        },
    });

    const challenges = query.data || [];
    
    // Computed values for all challenges
    const completedCount = challenges.filter(c => c.is_completed).length;
    const claimedCount = challenges.filter(c => c.is_claimed).length;
    const totalXpReward = challenges.reduce((sum, c) => sum + c.xp_reward, 0);
    const claimableXp = challenges
        .filter(c => c.is_completed && !c.is_claimed)
        .reduce((sum, c) => sum + c.xp_reward, 0);

    return useMemo(() => ({
        challenges,
        isLoading: query.isLoading,
        error: query.error,
        refetch: query.refetch,
        incrementProgress: incrementProgressMutation.mutateAsync,
        isIncrementing: incrementProgressMutation.isPending,
        claimReward: claimRewardMutation.mutateAsync,
        isClaiming: claimRewardMutation.isPending,
        completedCount,
        claimedCount,
        totalXpReward,
        claimableXp,
        allCompleted: completedCount === challenges.length && challenges.length > 0,
        allClaimed: claimedCount === challenges.length && challenges.length > 0,
    }), [challenges, query.isLoading, query.error, query.refetch, incrementProgressMutation.mutateAsync, incrementProgressMutation.isPending, claimRewardMutation.mutateAsync, claimRewardMutation.isPending, completedCount, claimedCount, totalXpReward, claimableXp]);
};

/**
 * @deprecated Use useDailyChallenges instead for multiple challenges
 * Legacy hook for backward compatibility - returns first challenge only
 */
export const useDailyChallenge = () => {
    const { userId } = useAuth();
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: queryKeys.dailyChallengeSingle.byUser(userId),
        enabled: !!userId,
        queryFn: async (): Promise<DailyChallenge | null> => {
            if (!userId) return null;

            const data = await apiGet<unknown[]>("/v1/gamification/challenges");
            const error = null;

            if (error) {
                console.error("Error fetching daily challenge:", error);
                return null;
            }

            // Return first challenge for backward compatibility
            return data?.[0] as DailyChallenge;
        },
        staleTime: 1000 * 60 * 2, // 2 minutes
    });

    const incrementProgressMutation = useMutation({
        mutationFn: async ({
            challengeType,
            increment = 1,
        }: {
            challengeType: ChallengeType;
            increment?: number;
        }): Promise<IncrementProgressResult | null> => {
            if (!userId) return null;

            const data = await apiPost<unknown[]>(
                `/v1/gamification/challenges/${encodeURIComponent(challengeType)}/progress`,
                { increment },
            );
            const error = null;

            if (error) {
                console.error("Error incrementing challenge progress:", error);
                throw error;
            }

            return data?.[0] as IncrementProgressResult;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.dailyChallengeSingle.byUser(userId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.dailyChallenges.byUser(userId) });
        },
    });

    const claimRewardMutation = useMutation({
        mutationFn: async (challengeId?: string): Promise<ClaimRewardResult | null> => {
            if (!userId) return null;

            const data = await apiPost<ClaimRewardResult>(
                `/v1/gamification/challenges/${encodeURIComponent(challengeId ?? "")}/claims`,
                {}
            );
            return (data ?? null) as ClaimRewardResult | null;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.dailyChallengeSingle.byUser(userId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.dailyChallenges.byUser(userId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.gamification.byUser(userId) });
        },
    });

    const challenge = query.data;
    const progress = challenge
        ? Math.min(100, (challenge.current_count / challenge.target_count) * 100)
        : 0;

    // Single-challenge shape: this hook predates `useDailyChallenges` and returns
    // the first row plus its progress, not the collection.
    return useMemo(() => ({
        challenge,
        progress,
        isLoading: query.isLoading,
        error: query.error,
        refetch: query.refetch,
        incrementProgress: incrementProgressMutation.mutateAsync,
        isIncrementing: incrementProgressMutation.isPending,
        claimReward: claimRewardMutation.mutateAsync,
        isClaiming: claimRewardMutation.isPending,
    }), [challenge, progress, query.isLoading, query.error, query.refetch, incrementProgressMutation.mutateAsync, incrementProgressMutation.isPending, claimRewardMutation.mutateAsync, claimRewardMutation.isPending]);
};
